import * as React from 'react';
import { Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { VatReportData } from 'store/vat/models';
import { TaxReportSubsectionDataResponse } from 'store/vat/models/vatReportData';
import { MappingTaxRate } from 'store/returns/models/mappingTaxRate';
import { Section } from 'store/returns/models/returnData';
import { getRawNumberForCopy, useNumberFormat } from 'utils/numberFormat';
import { OSSSubsectionConfig, resolveVisibleOssCountryTables } from './oss-country-tables';

interface OSSFormProps {
  data?: VatReportData;
  renderValueCell: (boxNumber: string, withP?: boolean) => React.ReactNode;
  finalTotals: boolean;
  jsonMapping?: MappingTaxRate[];
  mappingSections?: Section[];
  returnTypeId?: string;
}

const TAXABLE_AMOUNT_HEADER_STYLE: React.CSSProperties = {
  width: '140px',
  textAlign: 'center',
};

const VAT_AMOUNT_HEADER_STYLE: React.CSSProperties = {
  width: '130px',
  textAlign: 'center',
};

const OssCollapseWrapper = styled.div`
  .oss-section-controls {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 13px;
  }

  .oss-section-control-link {
    background: none;
    border: none;
    padding: 0;
    color: var(--color-primary, #41aaaf);
    cursor: pointer;
    font-size: 13px;
    font-family: var(--font-family, Inter, sans-serif);

    &:hover {
      text-decoration: underline;
    }
  }

  .oss-section-control-divider {
    color: var(--color-text-muted, #879494);
  }

  .oss-sections-collapse {
    background: transparent;
    border: none;

    > .ant-collapse-item {
      margin-bottom: 12px;
      background: #ffffff;
      border: 1px solid #dbeae3 !important;
      border-radius: 10px !important;
      overflow: hidden;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .ant-collapse-header {
      padding: 14px 20px !important;
      align-items: center !important;
      background: #eaf6f7 !important;
      color: var(--color-text-muted, #5a6a6a) !important;
      border-bottom: 1px solid #dbeae3 !important;
    }

    .ant-collapse-expand-icon {
      color: var(--color-primary, #41aaaf) !important;
    }

    .ant-collapse-content {
      border-top: none !important;
    }

    .ant-collapse-content-box {
      padding: 0 !important;
    }
  }

  .oss-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding-right: 8px;
  }

  .oss-section-title {
    font-size: 16px;
    font-weight: 600;
    font-family: var(--font-family, Inter, sans-serif);
    line-height: 22px;
    text-align: left;
    color: var(--color-text-muted, #5a6a6a);
  }

  .oss-section-table.vatreporttable {
    margin-bottom: 0;
    border-color: #dbeae3 !important;
    color: var(--color-text-muted, #5a6a6a);
  }

  .oss-section-table.vatreporttable thead tr {
    border-radius: 0;
    background-color: #f3f7f8 !important;
    color: var(--color-text-muted, #5a6a6a) !important;
    border: none !important;
    border-bottom: 1px solid #dbeae3 !important;
  }

  .oss-section-table.vatreporttable thead:first-child > tr:first-child > th {
    border-radius: 0 !important;
    border-top: none !important;
    border-bottom: 1px solid #dbeae3 !important;
    color: var(--color-text-muted, #5a6a6a) !important;
    font-weight: 600 !important;
  }

  .oss-section-table.vatreporttable tbody td {
    border-color: #dbeae3 !important;
    color: var(--color-text-muted, #5a6a6a);
  }

  .oss-section-table.table > tbody > tr > td {
    border-top-color: #dbeae3 !important;
  }

  .oss-section-table.table > :not(:last-child) > :last-child > * {
    border-bottom-color: #dbeae3 !important;
  }
`;

const toPlaceholder = (value: string) => `-${value}-`;

const toBoxKey = (boxCode: string) =>
  boxCode.replaceAll(' ', '-').replaceAll('(', '').replaceAll(')', '').toLowerCase();

interface OssSubsectionRowProps {
  row: OSSSubsectionConfig;
  renderValueCell: OSSFormProps['renderValueCell'];
}

const OssSubsectionRow: React.FC<OssSubsectionRowProps> = ({
  row,
  renderValueCell,
}) => {
  const boxKey = toBoxKey(row.boxCode);

  return (
    <tr>
      <td id={`subsection${boxKey}-name`}>{toPlaceholder(row.name)}</td>
      <td id={`subsection${boxKey}-box-name`} className="box">
        {toPlaceholder(row.boxCode)}
      </td>
      <td
        id={`subsection${boxKey}-value`}
        className="rightalign value-field"
      >
        {renderValueCell(boxKey)}
      </td>
      <td
        id={`subsection${boxKey}vat-value`}
        className="rightalign value-field"
      >
        {renderValueCell(`${boxKey}vat`)}
      </td>
    </tr>
  );
};

interface OssCountryTableProps {
  rows: OSSSubsectionConfig[];
  renderValueCell: OSSFormProps['renderValueCell'];
}

const OssCountryTable: React.FC<OssCountryTableProps> = ({
  rows,
  renderValueCell,
}) => (
  <table className="vatreporttable table oss-section-table">
    <thead>
      <tr>
        <th />
        <th />
        <th style={TAXABLE_AMOUNT_HEADER_STYLE}>
          Taxable Amount - <span className="currency-sign" />
        </th>
        <th style={VAT_AMOUNT_HEADER_STYLE}>
          VAT Amount - <span className="currency-sign" />
        </th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <OssSubsectionRow
          key={row.boxCode}
          row={row}
          renderValueCell={renderValueCell}
        />
      ))}
    </tbody>
  </table>
);

export const OSSForm: React.FC<OSSFormProps> = ({
  data,
  renderValueCell,
  finalTotals,
  jsonMapping,
  mappingSections,
  returnTypeId,
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useNumberFormat();
  const visibleCountryTables = React.useMemo(
    () =>
      resolveVisibleOssCountryTables(
        jsonMapping,
        mappingSections,
        returnTypeId,
        data?.sections?.map((section) => section.name),
        data?.subsections?.map((subsection) => subsection.code)
      ),
    [jsonMapping, mappingSections, returnTypeId, data?.sections, data?.subsections]
  );

  const countryCodesKey = visibleCountryTables.map((c) => c.code).join(',');

  const [activeCountryKeys, setActiveCountryKeys] = React.useState<string[]>(
    () =>
      visibleCountryTables.length > 0 ? [visibleCountryTables[0].code] : []
  );

  React.useEffect(() => {
    setActiveCountryKeys(
      visibleCountryTables.length > 0 ? [visibleCountryTables[0].code] : []
    );
  }, [countryCodesKey]);

  React.useEffect(() => {
    const selectedSubsections:
      | TaxReportSubsectionDataResponse[]
      | null
      | undefined = !finalTotals
      ? data?.subsections
      : data?.adjustedSubsections;
    if (data === undefined || data === null) return;

    const currencySignSpans = document.querySelectorAll('.currency-sign');
    currencySignSpans.forEach((currencySignSpan) => {
      if (data.currencySymbol !== null) {
        currencySignSpan.textContent = data.currencySymbol;
      }
    });

    data.sections?.forEach((section, i) => {
      const sectionNameTargets = document.querySelectorAll(
        `#section10${i}-name, #section10${i}-header-name`
      );
      sectionNameTargets.forEach((el) => {
        el.textContent = section.name;
      });
    });

    selectedSubsections?.forEach((subsection) => {
      const boxNumber = toBoxKey(subsection.code);

      const subsectionNameCell = document.getElementById(
        `subsection${boxNumber}-name`
      );
      if (subsectionNameCell) {
        subsectionNameCell.textContent = subsection.name;
      }
      const subsectionBoxNameCell = document.getElementById(
        `subsection${boxNumber}-box-name`
      );
      if (subsectionBoxNameCell) {
        subsectionBoxNameCell.textContent = subsection.code;
      }
      const subsectionValueSpan = document.getElementById(
        `subsection${boxNumber}-value-span`
      );
      if (subsectionValueSpan) {
        const textContent: any = subsection.net;
        subsectionValueSpan.textContent = formatNumber(textContent);
        subsectionValueSpan.setAttribute(
          'data-raw-value',
          getRawNumberForCopy(textContent)
        );
      }
    });
  }, [data, finalTotals, formatNumber, activeCountryKeys]);

  const expandAllCountries = () => {
    setActiveCountryKeys(visibleCountryTables.map((country) => country.code));
  };

  const collapseAllCountries = () => {
    setActiveCountryKeys([]);
  };

  const collapseItems = visibleCountryTables.map((country) => ({
    key: country.code,
    // Keep collapsed country tables in the DOM so the report's HTML
    // value/name update pass can fill them without expanding first.
    forceRender: true,
    label: (
      <div className="oss-section-header">
        <span
          id={`section10${country.sectionIndex}-header-name`}
          className="oss-section-title"
        >
          {toPlaceholder(country.name)}
        </span>
        {/* Kept for any report scripts still targeting the original section id. */}
        <span id={`section10${country.sectionIndex}-name`} hidden>
          {toPlaceholder(country.name)}
        </span>
      </div>
    ),
    children: (
      <OssCountryTable
        rows={country.rows}
        renderValueCell={renderValueCell}
      />
    ),
  }));

  return (
    <OssCollapseWrapper>
      {visibleCountryTables.length > 0 && (
        <div className="oss-section-controls">
          <button
            type="button"
            className="oss-section-control-link"
            onClick={expandAllCountries}
          >
            {t('returns.mapping.expand-all-sections')}
          </button>
          <span className="oss-section-control-divider">|</span>
          <button
            type="button"
            className="oss-section-control-link"
            onClick={collapseAllCountries}
          >
            {t('returns.mapping.collapse-all-sections')}
          </button>
        </div>
      )}
      <Collapse
        className="oss-sections-collapse"
        bordered={false}
        expandIconPosition="end"
        activeKey={activeCountryKeys}
        onChange={(keys) =>
          setActiveCountryKeys(
            Array.isArray(keys) ? keys : keys ? [keys] : []
          )
        }
        destroyInactivePanel={false}
        items={collapseItems}
      />
    </OssCollapseWrapper>
  );
};
